import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AudioButton } from "@/components/AudioButton";
import {
  ArrowLeft,
  Trash2,
  Play,
  ChevronRight,
  AlertCircle,
  Edit2,
  X,
  Save,
  Plus,
  Search,
  Check,
  Sparkles,
  Loader2,
  Shuffle,
  Lock,
  FileUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getStudySet,
  getStudySetWords,
  getWords,
  deleteStudySet,
  updateStudySet,
  removeWordFromStudySet,
  addWordToStudySet,
  addWord,
  addWordWithGrammar,
} from "@/hooks/useFirestore";
import { generateVocabularyWords, GeneratedWord } from "@/hooks/useDirectApi";
import { ExportDialog } from "@/components/ExportDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
// TODO: Russian support hidden — re-enable useUserPreferences for language detection
// import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { Word } from "@/types";
import { calculateStrength, cn } from "@/lib/utils";
import { enrichGrammarInBackground } from "@/lib/grammarEnrich";

export function StudySetDetailPage() {
  const { user } = useAuth();
  const { canUseFeature, openUpgradePrompt, isPro } = useSubscription();
  // TODO: Russian support hidden — preferences was used for language detection
  const { studySetId } = useParams<{ studySetId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showAddWordsDialog, setShowAddWordsDialog] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [addWordsSearch, setAddWordsSearch] = useState("");
  const [selectedWordsToAdd, setSelectedWordsToAdd] = useState<string[]>([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [isShuffled, setIsShuffled] = useState(false);

  // Create new word state
  const [newWordHebrew, setNewWordHebrew] = useState("");
  const [newWordTranslation, setNewWordTranslation] = useState("");
  const [isCreatingWord, setIsCreatingWord] = useState(false);

  // AI Generation state
  const [generateTopic, setGenerateTopic] = useState("");
  const [generateDescription, setGenerateDescription] = useState("");
  const [generateCount, setGenerateCount] = useState(10);
  const [generatedWords, setGeneratedWords] = useState<GeneratedWord[]>([]);
  const [generationStep, setGenerationStep] = useState<"input" | "preview" | "adding">("input");
  const [addingProgress, setAddingProgress] = useState({ current: 0, total: 0, added: 0, existing: 0 });

  const { data: studySet, isLoading: setLoading } = useQuery({
    queryKey: ["studySet", studySetId],
    queryFn: () => getStudySet(user!.uid, studySetId!),
    enabled: !!user && !!studySetId,
  });

  const { data: words, isLoading: wordsLoading } = useQuery({
    queryKey: ["studySetWords", user?.uid, studySetId],
    queryFn: () => getStudySetWords(user!.uid, studySetId!),
    enabled: !!user && !!studySetId,
  });

  // Get all words for add dialog
  const { data: allWords } = useQuery({
    queryKey: ["words", user?.uid],
    queryFn: () => getWords(user!.uid),
    enabled: !!user && showAddWordsDialog,
  });

  // Filter words not already in study set
  const availableWords = useMemo(() => {
    if (!allWords || !studySet) return [];
    return allWords.filter((w) => !studySet.wordIds.includes(w.id));
  }, [allWords, studySet]);

  // Filter by search
  const filteredAvailableWords = useMemo(() => {
    if (!addWordsSearch) return availableWords;
    const query = addWordsSearch.toLowerCase();
    return availableWords.filter(
      (w) =>
        w.hebrew.text.includes(addWordsSearch) ||
        w.translations.en?.text.toLowerCase().includes(query) ||
        w.translations.ru?.text?.toLowerCase().includes(query)
    );
  }, [availableWords, addWordsSearch]);

  // Fisher-Yates shuffle
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Shuffled words display
  const [shuffledWords, setShuffledWords] = useState<Word[]>([]);

  const displayWords = useMemo(() => {
    if (!words) return [];
    if (isShuffled && shuffledWords.length > 0) return shuffledWords;
    return words;
  }, [words, isShuffled, shuffledWords]);

  const handleShuffle = () => {
    if (words) {
      setShuffledWords(shuffleArray(words));
      setIsShuffled(true);
    }
  };

  const handleUnshuffle = () => {
    setIsShuffled(false);
    setShuffledWords([]);
  };

  // Calculate stats
  const stats = useMemo(() => {
    if (!words) return { wordCount: 0, reviewedCount: 0, successRate: 0 };

    let reviewedCount = 0;
    let totalKnown = 0;
    let totalUnknown = 0;

    words.forEach((word) => {
      const reviewed = word.reviewStats.knownCount + word.reviewStats.unknownCount;
      if (reviewed > 0) {
        reviewedCount++;
        totalKnown += word.reviewStats.knownCount;
        totalUnknown += word.reviewStats.unknownCount;
      }
    });

    const totalReviews = totalKnown + totalUnknown;
    const successRate = totalReviews > 0 ? Math.round((totalKnown / totalReviews) * 100) : 0;

    return {
      wordCount: words.length,
      reviewedCount,
      successRate,
    };
  }, [words]);

  const deleteMutation = useMutation({
    mutationFn: () => deleteStudySet(user!.uid, studySetId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      navigate("/scenarios");
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateStudySet(user!.uid, studySetId!, {
        name: editedName,
        description: editedDescription,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studySet", studySetId] });
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      setIsEditingName(false);
    },
  });

  const removeWordMutation = useMutation({
    mutationFn: (wordId: string) => removeWordFromStudySet(user!.uid, studySetId!, wordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studySetWords"] });
      queryClient.invalidateQueries({ queryKey: ["studySet", studySetId] });
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
    },
  });

  const addWordsMutation = useMutation({
    mutationFn: async (wordIds: string[]) => {
      for (const wordId of wordIds) {
        await addWordToStudySet(user!.uid, studySetId!, wordId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["studySetWords"] });
      queryClient.invalidateQueries({ queryKey: ["studySet", studySetId] });
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      setShowAddWordsDialog(false);
      setSelectedWordsToAdd([]);
      setAddWordsSearch("");
    },
  });

  const toggleWordSelection = (wordId: string) => {
    setSelectedWordsToAdd((prev) =>
      prev.includes(wordId) ? prev.filter((id) => id !== wordId) : [...prev, wordId]
    );
  };

  const handleAddSelectedWords = () => {
    if (selectedWordsToAdd.length > 0) {
      addWordsMutation.mutate(selectedWordsToAdd);
    }
  };

  const handleCreateWord = async () => {
    if (!user || !newWordHebrew.trim() || !newWordTranslation.trim()) return;
    setIsCreatingWord(true);
    try {
      // TODO: Russian support hidden — re-enable by checking for "ru" in direction
      const lang = "en";
      const wordId = await addWordWithGrammar(
        user.uid,
        newWordHebrew.trim(),
        newWordTranslation.trim(),
        lang,
        [],
        undefined
      );
      setSelectedWordsToAdd((prev) =>
        prev.includes(wordId) ? prev : [...prev, wordId]
      );
      queryClient.invalidateQueries({ queryKey: ["words", user.uid] });
      setNewWordHebrew("");
      setNewWordTranslation("");
    } catch (err) {
      console.error("Failed to create word:", err);
    } finally {
      setIsCreatingWord(false);
    }
  };

  // AI Generation mutations
  const generateMutation = useMutation({
    mutationFn: () =>
      generateVocabularyWords(generateTopic || studySet?.name || "", generateDescription, generateCount),
    onSuccess: (words) => {
      setGeneratedWords(words);
      setGenerationStep("preview");
    },
  });

  const addGeneratedWordsMutation = useMutation({
    mutationFn: async () => {
      if (!user || !studySetId) throw new Error("Not authenticated");

      setGenerationStep("adding");
      const results = { added: 0, existing: 0, addedWordIds: [] as { wordId: string; hebrew: string }[] };
      setAddingProgress({ current: 0, total: generatedWords.length, added: 0, existing: 0 });

      // Read study set once before the loop instead of every iteration
      const currentSet = await getStudySet(user.uid, studySetId!);
      const existingWordIds = new Set(currentSet?.wordIds || []);

      for (let i = 0; i < generatedWords.length; i++) {
        const word = generatedWords[i];
        try {
          // addWord handles duplicates - it will return existing word ID if duplicate
          const wordId = await addWord(
            user.uid,
            word.hebrew,
            word.english,
            "en",
            word.category ? [word.category] : []
          );

          results.addedWordIds.push({ wordId, hebrew: word.hebrew });

          if (!existingWordIds.has(wordId)) {
            await addWordToStudySet(user.uid, studySetId!, wordId);
            existingWordIds.add(wordId);
            results.added++;
          } else {
            results.existing++;
          }
        } catch (error) {
          console.error("Error adding word:", word.hebrew, error);
        }

        setAddingProgress({
          current: i + 1,
          total: generatedWords.length,
          added: results.added,
          existing: results.existing,
        });
      }

      return results;
    },
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: ["studySetWords"] });
      queryClient.invalidateQueries({ queryKey: ["studySet", studySetId] });
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      queryClient.invalidateQueries({ queryKey: ["words"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });

      // Background grammar enrichment — fire and forget
      if (user && results.addedWordIds.length > 0) {
        enrichGrammarInBackground(user.uid, results.addedWordIds, queryClient).catch((err) =>
          console.error("[grammar-enrich] Background enrichment failed:", err)
        );
      }

      // Auto-close dialog after showing checkmark briefly
      setTimeout(() => resetGenerateDialog(), 1500);
    },
  });

  const resetGenerateDialog = () => {
    setShowGenerateDialog(false);
    setGenerateTopic("");
    setGenerateDescription("");
    setGenerateCount(10);
    setGeneratedWords([]);
    setGenerationStep("input");
    setAddingProgress({ current: 0, total: 0, added: 0, existing: 0 });
  };

  const startEditing = () => {
    setEditedName(studySet?.name || "");
    setEditedDescription(studySet?.description || "");
    setIsEditingName(true);
  };

  if (setLoading || wordsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!studySet) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Study set not found</p>
        <Button variant="ghost" onClick={() => navigate("/scenarios")} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Study Sets
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/scenarios")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            onClick={() => setShowExportDialog(true)}
            disabled={!words || words.length === 0}
          >
            <FileUp className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Study Set Header */}
      <Card>
        <CardContent className="pt-6">
          {isEditingName ? (
            <div className="space-y-4">
              <Input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder="Study set name"
                className="text-xl font-semibold text-center"
                autoFocus
              />
              <Input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                placeholder="Description (optional)"
                className="text-center"
              />
              <div className="flex justify-center gap-2">
                <Button
                  size="sm"
                  onClick={() => updateMutation.mutate()}
                  disabled={updateMutation.isPending}
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIsEditingName(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2">
                <h1 className="text-2xl font-semibold">{studySet.name}</h1>
                <Button variant="ghost" size="sm" onClick={startEditing}>
                  <Edit2 className="h-4 w-4" />
                </Button>
              </div>
              {studySet.description && (
                <p className="text-muted-foreground">{studySet.description}</p>
              )}
              <p className="text-muted-foreground">{stats.wordCount} words</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats.wordCount}</div>
            <div className="text-sm text-muted-foreground">Words</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats.reviewedCount}</div>
            <div className="text-sm text-muted-foreground">Reviewed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{stats.successRate}%</div>
            <div className="text-sm text-muted-foreground">Success</div>
          </CardContent>
        </Card>
      </div>

      {/* Review Button */}
      <Button
        className="w-full"
        size="lg"
        onClick={() => navigate(`/practice?studySet=${studySetId}`)}
        disabled={stats.wordCount === 0}
      >
        <Play className="mr-2 h-5 w-5" />
        Review This Set
      </Button>

      {/* All Words */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">
            Words in Set
            {isShuffled && <span className="text-sm font-normal text-muted-foreground ml-2">(shuffled)</span>}
          </CardTitle>
          <div className="flex gap-2">
            {words && words.length > 1 && (
              <Button
                size="sm"
                variant={isShuffled ? "default" : "outline"}
                onClick={isShuffled ? handleUnshuffle : handleShuffle}
              >
                <Shuffle className="h-4 w-4 mr-1" />
                {isShuffled ? "Unshuffle" : "Shuffle"}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (!canUseFeature("vocabulary")) {
                  openUpgradePrompt("vocabulary");
                  return;
                }
                setShowGenerateDialog(true);
              }}
            >
              {isPro ? <Sparkles className="h-4 w-4 mr-1" /> : <Lock className="h-4 w-4 mr-1" />}
              AI Generate
            </Button>
            <Button size="sm" onClick={() => setShowAddWordsDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Words
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {displayWords.length === 0 ? (
            <div className="text-center text-muted-foreground py-4 space-y-2">
              <p>No words in this study set yet.</p>
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!canUseFeature("vocabulary")) {
                      openUpgradePrompt("vocabulary");
                      return;
                    }
                    setShowGenerateDialog(true);
                  }}
                >
                  {isPro ? <Sparkles className="h-4 w-4 mr-1" /> : <Lock className="h-4 w-4 mr-1" />}
                  AI Generate Words
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddWordsDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add from Word Bank
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {displayWords.map((word) => (
                <WordRow
                  key={word.id}
                  word={word}
                  onRemove={() => removeWordMutation.mutate(word.id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        words={words || []}
        name={studySet.name}
      />

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Study Set</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{studySet.name}"? The words will remain in your
              word bank.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Words Dialog */}
      <Dialog
        open={showAddWordsDialog}
        onOpenChange={(open) => {
          setShowAddWordsDialog(open);
          if (!open) {
            setSelectedWordsToAdd([]);
            setAddWordsSearch("");
            setNewWordHebrew("");
            setNewWordTranslation("");
          }
        }}
      >
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Words to Study Set</DialogTitle>
            <DialogDescription>
              Select words from your word bank or create new ones
            </DialogDescription>
          </DialogHeader>

          {/* Create New Word */}
          <div className="space-y-2 pb-3 border-b">
            <div className="text-sm font-medium">Create New Word</div>
            <div className="flex gap-2">
              <Input
                value={newWordHebrew}
                onChange={(e) => setNewWordHebrew(e.target.value)}
                placeholder="...עברית"
                className="hebrew-text flex-1"
                dir="rtl"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newWordHebrew.trim() && newWordTranslation.trim()) {
                    e.preventDefault();
                    handleCreateWord();
                  }
                }}
              />
              <Input
                value={newWordTranslation}
                onChange={(e) => setNewWordTranslation(e.target.value)}
                placeholder="English..."
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newWordHebrew.trim() && newWordTranslation.trim()) {
                    e.preventDefault();
                    handleCreateWord();
                  }
                }}
              />
              <Button
                size="icon"
                variant="outline"
                onClick={handleCreateWord}
                disabled={!newWordHebrew.trim() || !newWordTranslation.trim() || isCreatingWord}
                title="Create word and select it"
              >
                {isCreatingWord ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={addWordsSearch}
              onChange={(e) => setAddWordsSearch(e.target.value)}
              placeholder="Search words..."
              className="pl-9"
            />
          </div>

          {/* Word List */}
          <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[300px] border rounded-md">
            {filteredAvailableWords.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {availableWords.length === 0
                  ? "All words are already in this study set"
                  : "No words match your search"}
              </p>
            ) : (
              <div className="divide-y">
                {filteredAvailableWords.map((word) => {
                  const isSelected = selectedWordsToAdd.includes(word.id);
                  return (
                    <div
                      key={word.id}
                      onClick={() => toggleWordSelection(word.id)}
                      className={cn(
                        "flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50",
                        isSelected && "bg-primary/10"
                      )}
                    >
                      <div
                        className={cn(
                          "w-5 h-5 rounded border flex items-center justify-center",
                          isSelected
                            ? "bg-primary border-primary text-primary-foreground"
                            : "border-muted-foreground"
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <span className="font-medium hebrew-text" dir="rtl">
                        {word.hebrew.text}
                      </span>
                      <span className="text-muted-foreground text-sm">
                        {word.translations.en?.text || word.translations.ru?.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="flex-row justify-between sm:justify-between">
            <span className="text-sm text-muted-foreground">
              {selectedWordsToAdd.length} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAddWordsDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddSelectedWords}
                disabled={selectedWordsToAdd.length === 0 || addWordsMutation.isPending}
              >
                {addWordsMutation.isPending
                  ? "Adding..."
                  : `Add ${selectedWordsToAdd.length} Word${selectedWordsToAdd.length !== 1 ? "s" : ""}`}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate Words Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={(open) => !open && resetGenerateDialog()}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              AI Generate Words
            </DialogTitle>
            <DialogDescription>
              {generationStep === "input" &&
                "Describe what vocabulary you want to learn and AI will generate words for you"}
              {generationStep === "preview" &&
                "Review the generated words before adding them to your study set"}
              {generationStep === "adding" && "Adding words to your study set..."}
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Input */}
          {generationStep === "input" && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Topic</label>
                <Input
                  value={generateTopic}
                  onChange={(e) => setGenerateTopic(e.target.value)}
                  placeholder={studySet?.name || "e.g., Body parts, Kitchen items, Travel"}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use the study set name: "{studySet?.name}"
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <textarea
                  value={generateDescription}
                  onChange={(e) => setGenerateDescription(e.target.value)}
                  placeholder="Describe what you want to learn. e.g., 'Common words for cooking and kitchen activities' or 'Basic medical terms for a doctor visit'"
                  className="w-full min-h-[80px] p-2 border rounded-md text-sm resize-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Number of words</label>
                <div className="flex gap-2">
                  {[5, 10, 15, 20].map((n) => (
                    <Button
                      key={n}
                      size="sm"
                      variant={generateCount === n ? "default" : "outline"}
                      onClick={() => setGenerateCount(n)}
                    >
                      {n}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {generationStep === "preview" && (
            <div className="flex-1 overflow-y-auto min-h-[200px] max-h-[350px] border rounded-md">
              <div className="divide-y">
                {generatedWords.map((word, index) => (
                  <div key={index} className="flex items-center gap-3 p-3">
                    <span className="font-medium hebrew-text text-lg" dir="rtl">
                      {word.hebrew}
                    </span>
                    <span className="text-muted-foreground">{word.english}</span>
                    {word.category && (
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {word.category}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Adding */}
          {generationStep === "adding" && (
            <div className="py-8 space-y-4">
              <div className="flex justify-center">
                {addGeneratedWordsMutation.isSuccess ? (
                  <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                ) : (
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                )}
              </div>
              <div className="text-center space-y-2">
                <p className="font-medium">
                  {addGeneratedWordsMutation.isSuccess
                    ? `Added ${addingProgress.added} words!`
                    : `Adding words... ${addingProgress.current} / ${addingProgress.total}`}
                </p>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{
                      width: `${(addingProgress.current / addingProgress.total) * 100}%`,
                    }}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  {addingProgress.added} new words added
                  {addingProgress.existing > 0 && `, ${addingProgress.existing} already in set`}
                </p>
              </div>
            </div>
          )}

          {/* Footer */}
          <DialogFooter>
            {generationStep === "input" && (
              <>
                <Button variant="outline" onClick={resetGenerateDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={() => generateMutation.mutate()}
                  disabled={!generateDescription.trim() || generateMutation.isPending}
                >
                  {generateMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate {generateCount} Words
                    </>
                  )}
                </Button>
              </>
            )}
            {generationStep === "preview" && (
              <>
                <Button variant="outline" onClick={() => setGenerationStep("input")}>
                  Back
                </Button>
                <Button onClick={() => addGeneratedWordsMutation.mutate()}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add {generatedWords.length} Words to Set
                </Button>
              </>
            )}
            {generationStep === "adding" && addGeneratedWordsMutation.isSuccess && (
              <p className="text-sm text-muted-foreground">Closing...</p>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WordRow({ word, onRemove }: { word: Word; onRemove: () => void }) {
  const strength = calculateStrength(
    word.reviewStats.knownCount,
    word.reviewStats.unknownCount
  );

  return (
    <div className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50">
      <Link to={`/words/${word.id}`} className="flex items-center gap-3 flex-1">
        <div
          className={cn(
            "w-2 h-2 rounded-full",
            strength === "weak" && "bg-red-500",
            strength === "learning" && "bg-amber-500",
            strength === "strong" && "bg-green-500"
          )}
        />
        <span className="font-medium hebrew-text" dir="rtl">
          {word.hebrew.text}
        </span>
        <AudioButton text={word.hebrew.text} size="sm" />
        <span className="text-muted-foreground">
          {word.translations.en?.text || word.translations.ru?.text}
        </span>
      </Link>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
        >
          <X className="h-4 w-4" />
        </Button>
        <Link to={`/words/${word.id}`}>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      </div>
    </div>
  );
}
